import React from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FiHome, FiTv, FiSettings, FiTruck,
  FiMap, FiFileText, FiAlertCircle, FiWifi,
  FiLock, FiPhone, FiNavigation, FiAlertTriangle,
  FiMail, FiUser, FiActivity, FiThermometer,
  FiChevronRight, FiCopy
} from 'react-icons/fi';
import { IoCarOutline, IoBedOutline } from 'react-icons/io5';
import { MdHotTub, MdDownhillSkiing } from 'react-icons/md';
import toast from 'react-hot-toast';
import { api } from '../api';

const iconMap = {
  home: FiHome,
  tv: FiTv,
  settings: FiSettings,
  bed: IoBedOutline,
  car: FiTruck,
  map: FiMap,
  list: FiFileText,
  alert: FiAlertCircle,
  wifi: FiWifi,
  thermometer: FiThermometer,
  thermostat: FiThermometer,
  activity: FiActivity,
  activities: FiActivity,
  'hot-tub': MdHotTub,
  skiing: MdDownhillSkiing,
};

const QuickInfoCard = ({ icon: Icon, title, content, color = "bg-lodge-brown-100", priority = 2, isRichText = false }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    className={`bg-white/90 backdrop-blur-sm rounded-2xl shadow-sm p-4 sm:p-5 hover:shadow-md transition-all duration-200 h-full relative overflow-hidden ${
      priority === 1 ? 'border-2' : 'border'
    } border-white/50`}
  >
    {/* Priority indicator bar */}
    <div className={`absolute top-0 left-0 right-0 h-1 ${
      priority === 1 ? 'bg-gradient-to-r from-red-400 to-red-500' :
      priority === 3 ? 'bg-gray-300' :
      'bg-gradient-to-r from-blue-400 to-blue-500'
    }`} />
    {/* Horizontal layout on all sizes; vertical on sm+ for grid */}
    <div className="flex flex-row sm:flex-col items-start sm:items-center h-full">
      <div className={`w-10 h-10 sm:w-14 sm:h-14 ${color} rounded-xl flex items-center justify-center bg-opacity-60 shrink-0`}>
        <Icon className="w-5 h-5 sm:w-7 sm:h-7 text-lodge-brown-700" />
      </div>
      <div className="ml-3 sm:ml-0 sm:mt-4 flex-grow min-w-0">
        <h3 className={`text-mountain-gray-900 text-base sm:text-lg mb-1 sm:mb-3 text-left sm:text-center ${
          priority === 1 ? 'font-bold' : 'font-semibold'
        }`}>{title}</h3>
        {isRichText ? (
          <div
            className="text-mountain-gray-700 text-[15px] sm:text-[15px] leading-relaxed text-left sm:max-w-[200px] prose prose-sm prose-ul:my-1 prose-li:my-0 prose-p:my-1 [&_ul]:list-disc [&_ul]:ml-4 [&_strong]:font-semibold [&_strong]:text-mountain-gray-900"
            dangerouslySetInnerHTML={{ __html: content }}
          />
        ) : (
          <div className="text-mountain-gray-700 text-[15px] sm:text-[15px] leading-relaxed sm:leading-[1.75] text-left sm:max-w-[200px] space-y-0.5 sm:space-y-1">
            {content}
          </div>
        )}
      </div>
    </div>
  </motion.div>
);

const copyToClipboard = (text) => {
  navigator.clipboard.writeText(text);
  toast.success('Copied to clipboard');
};

const HomePage = () => {
  const { data: categoriesResponse, isLoading } = useQuery('categories', () => api.getCategories());
  const { data: quickInfoResponse } = useQuery('quickInfoItems', () => api.getQuickInfoItems());

  const categories = categoriesResponse?.data;
  const quickInfoItems = quickInfoResponse?.data || [];

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lodge-brown-600"></div>
      </div>
    );
  }

  // Map icon strings to components
  const iconComponentMap = {
    alert: FiAlertTriangle,
    wifi: FiWifi,
    lock: FiLock,
    phone: FiPhone,
    navigation: FiNavigation,
    home: FiHome,
    mail: FiMail,
    user: FiUser,
    car: IoCarOutline,
    vehicle: IoCarOutline
  };

  // Map color strings
  const colorMap = {
    alert: "bg-red-200",
    wifi: "bg-sky-blue-100",
    lock: "bg-pine-green-100",
    phone: "bg-red-100",
    navigation: "bg-mountain-gray-100",
    home: "bg-lodge-brown-100",
    mail: "bg-blue-100",
    user: "bg-gray-100",
    car: "bg-indigo-100",
    vehicle: "bg-indigo-100"
  };

  // Process quick info items for display and sort by priority
  const processedQuickInfoItems = quickInfoItems
    .sort((a, b) => {
      // Sort by priority first (1 = highest), then by order_index
      if (a.priority !== b.priority) {
        return (a.priority || 2) - (b.priority || 2);
      }
      return (a.order_index || 999) - (b.order_index || 999);
    })
    .map(item => {
      let formattedContent = item.content;
      let isRichText = item.is_rich_text === 1 || item.is_rich_text === true;

      // Special formatting for Phone/Emergency card
      if (item.icon === 'phone' && isRichText) {
        // Parse <p> elements and classify each one
        const pTags = item.content.match(/<p>([\s\S]*?)<\/p>/g) || [];
        const elements = pTags.map(p => {
          const inner = p.replace(/<\/?p>/g, '').trim();
          if (/^<br\s*\/?>$/.test(inner) || inner === '') return { type: 'spacer' };

          const telMatch = inner.match(/<a[^>]+href="tel:([^"]+)"[^>]*>(.*?)<\/a>/);
          if (telMatch) return { type: 'phone', href: telMatch[1], display: telMatch[2].replace(/<[^>]+>/g, '').trim() };

          const text = inner.replace(/<[^>]+>/g, '').trim();
          if (/^[\d\(\)\s\-\+\u200F‬]{7,}$/.test(text)) return { type: 'phone', href: null, display: text };

          const isBold = /<strong>/.test(inner);
          const hasColon = text.endsWith(':') || /:\s*$/.test(text);
          return { type: 'text', text: text.replace(/:\s*$/, '').trim(), isBold, hasColon };
        });

        // Build grouped structure: headers with contacts underneath
        const groups = [];
        let currentGroup = null;
        let currentContact = null;

        const flushContact = () => {
          if (currentContact) {
            if (!currentGroup) currentGroup = { header: null, contacts: [] };
            currentGroup.contacts.push(currentContact);
            currentContact = null;
          }
        };

        elements.forEach(el => {
          if (el.type === 'spacer') { flushContact(); return; }
          if (el.type === 'phone') {
            if (currentContact) {
              currentContact.phone = el.display;
              currentContact.href = el.href
                ? `tel:${el.href}`
                : `tel:${el.display.replace(/[\s\-\(\)\u200F‬]/g, '')}`;
            }
            return;
          }
          if (el.type === 'text') {
            if (el.isBold && !el.hasColon && !currentContact) {
              // Group header — flush previous group
              flushContact();
              if (currentGroup && currentGroup.contacts.length > 0) groups.push(currentGroup);
              currentGroup = { header: el.text, contacts: [] };
            } else {
              // Contact name
              flushContact();
              currentContact = { name: el.text, phone: null, href: null };
            }
          }
        });
        flushContact();
        if (currentGroup) groups.push(currentGroup);

        formattedContent = (
          <div className="space-y-3">
            {groups.map((group, gIdx) => (
              <div key={gIdx}>
                {group.header && (
                  <div className="text-xs font-semibold text-mountain-gray-500 uppercase tracking-wide mb-1">
                    {group.header}
                  </div>
                )}
                <div className="divide-y divide-gray-100">
                  {group.contacts.map((contact, cIdx) => (
                    <div key={cIdx} className="flex items-center justify-between py-1.5 first:pt-0 last:pb-0 gap-2">
                      <div className="min-w-0">
                        <div className="font-semibold text-mountain-gray-900 text-[15px]">{contact.name}</div>
                        {contact.phone && (
                          <a href={contact.href} className="text-sm text-mountain-gray-600 hover:text-blue-600">
                            {contact.phone}
                          </a>
                        )}
                      </div>
                      {contact.href && (
                        <a
                          href={contact.href}
                          className="sm:hidden inline-flex items-center gap-1 px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-700 rounded-full text-sm font-medium transition-colors whitespace-nowrap shrink-0"
                        >
                          <FiPhone className="w-3.5 h-3.5" />
                          Call &rsaquo;
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        );
        isRichText = false;
      }

      // Special formatting for WiFi content
      else if (item.icon === 'wifi') {
        let pairs = [];

        if (isRichText) {
          // Parse rich text HTML to extract label:value pairs
          const lines = item.content.split(/<\/p>/).map(line => {
            return line.replace(/<[^>]+>/g, '').trim();
          }).filter(l => l);

          lines.forEach(line => {
            const colonIdx = line.indexOf(':');
            if (colonIdx > -1) {
              pairs.push({
                label: line.substring(0, colonIdx).trim(),
                value: line.substring(colonIdx + 1).trim()
              });
            }
          });
        } else {
          const lines = item.content.split('\n').filter(line => line.trim());
          lines.forEach(line => {
            const colonIdx = line.indexOf(':');
            if (colonIdx > -1) {
              pairs.push({
                label: line.substring(0, colonIdx).trim(),
                value: line.substring(colonIdx + 1).trim()
              });
            } else {
              pairs.push({ label: null, value: line.trim() });
            }
          });
        }

        formattedContent = (
          <div className="space-y-1">
            {pairs.map((pair, idx) => (
              pair.label ? (
                <div key={idx} className="flex flex-col">
                  <span className="text-sm text-mountain-gray-600">{pair.label}:</span>
                  <div className="flex items-center gap-1.5">
                    <span className="font-mono text-[15px] font-medium text-mountain-gray-900">{pair.value}</span>
                    <button
                      onClick={() => copyToClipboard(pair.value)}
                      className="p-1 hover:bg-gray-100 rounded transition-colors text-mountain-gray-400 hover:text-mountain-gray-600"
                      title={`Copy ${pair.label}`}
                    >
                      <FiCopy className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ) : (
                <div key={idx} className="text-[15px]">{pair.value}</div>
              )
            ))}
          </div>
        );
        isRichText = false;
      }

      // Plain text fallback
      else if (!isRichText) {
        const lines = item.content.split('\n').filter(line => line.trim());
        formattedContent = (
          <div className="space-y-1">
            {lines.map((line, idx) => (
              <div key={idx}>{line}</div>
            ))}
          </div>
        );
      }

      return {
        icon: iconComponentMap[item.icon] || FiAlertCircle,
        title: item.title,
        content: isRichText ? item.content : formattedContent,
        color: colorMap[item.icon] || "bg-gray-100",
        priority: item.priority || 2,
        isRichText
      };
    });

  return (
    <div className="max-w-6xl mx-auto pb-24 sm:pb-20 lg:pb-0">
      {/* Quick Info Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mb-8 px-4"
      >
        <h2 className="text-xl font-semibold tracking-tight text-lodge-brown-800 mb-3 sm:text-2xl sm:text-center sm:font-display sm:mb-4">
          Quick Info
        </h2>
        <div className="space-y-3 sm:space-y-0 sm:grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 sm:gap-4">
          {processedQuickInfoItems.map((item, index) => (
            <QuickInfoCard key={index} {...item} />
          ))}
        </div>
      </motion.div>

      {/* Categories */}
      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="space-y-2 px-4 mb-20 lg:mb-0 lg:space-y-0 lg:grid lg:grid-cols-5 lg:gap-5"
      >
        {categories?.map((category) => {
          const Icon = iconMap[category.icon] || FiFileText;
          return (
            <motion.div key={category.id} variants={item} className="lg:h-full">
              <Link
                to={`/category/${category.slug}`}
                className="block group w-full lg:h-full"
              >
                {/* Mobile: list row */}
                <motion.div
                  whileTap={{ scale: 0.98 }}
                  className="flex items-center bg-white/90 backdrop-blur-sm rounded-xl px-4 py-3.5 min-h-[56px] border border-white/50 lg:hidden"
                >
                  <div className="w-10 h-10 bg-lodge-brown-100/60 rounded-xl flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-lodge-brown-600" />
                  </div>
                  <h3 className="text-base font-medium text-mountain-gray-900 ml-3 flex-1">
                    {category.name}
                  </h3>
                  <FiChevronRight className="w-5 h-5 text-mountain-gray-400 shrink-0" />
                </motion.div>

                {/* Desktop: centered grid tile */}
                <div className="hidden lg:flex bg-white/95 backdrop-blur-sm rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 transform group-hover:-translate-y-1 p-5 border border-white/50 h-full min-h-[140px] items-center justify-center">
                  <div className="flex flex-col items-center text-center">
                    <div className="w-16 h-16 bg-lodge-brown-100/60 rounded-2xl flex items-center justify-center mb-3 group-hover:bg-lodge-brown-200/60 transition-colors">
                      <Icon className="w-8 h-8 text-lodge-brown-600" />
                    </div>
                    <h3 className="font-semibold text-base text-mountain-gray-900 group-hover:text-lodge-brown-700 transition-colors leading-tight">
                      {category.name}
                    </h3>
                  </div>
                </div>
              </Link>
            </motion.div>
          );
        })}
      </motion.div>
    </div>
  );
};

export default HomePage;